<template>
    <modal :show="!!edit_subblock" :closable="!loading" @close="$emit('close_edit_subblock')">
        <div slot="header">
            <div class="modal-header">
                <h5 class="modal-title">{{ $t('blocks.edit_block') }}</h5>
                <button type="button" class="close" data-dismiss="modal" aria-label="Close"
                        @click="$emit('close_edit_subblock')"><span aria-hidden="true">×</span></button>
            </div>
        </div>

        <div slot="body">
            <div class="form-horizontal">
                <div class="form-group row" v-if="block.type === 3 /* 3 = speaker */">
                    <label class="form-control-label col-4">{{ $t('blocks.name') }}</label>
                    <div class="col-8">
                        <input type="text" v-model="request.name" class="form-control-sm form-control">
                        <form-errors :errors="errors" field="name" />
                    </div>
                </div>

                <!-- Photo settings -->
                <div class="form-group row" v-if="isPhotoBlock(edit_subblock) && !isFixedBlock(edit_subblock.parent_block)">
                    <label class="form-control-label col-4">{{ $t('blocks.photo_settings') }}</label>
                    <div class="col-8">
                        <div class="custom-control custom-radio">
                            <input type="radio" id="music" name="photo_calculation" class="custom-control-input"
                                   :value="'music'" v-model="request.photo_calculation" :disabled="!hasMusic()">
                            <label class="custom-control-label" for="music"
                                   v-tooltip="(!hasMusic()) ? $t('blocks.based_on_music_tooltip') : ''">
                                {{ $t('blocks.based_on_music') }}
                            </label>
                        </div>
                        <div class="custom-control custom-radio">
                            <input type="radio" id="manual" name="photo_calculation" class="custom-control-input"
                                   :value="'manual'" v-model="request.photo_calculation">
                            <label class="custom-control-label" for="manual">{{ $t('blocks.manual') }}</label>
                        </div>
                        <div class="custom-control custom-radio">
                            <input type="radio" id="custom" name="photo_calculation" class="custom-control-input"
                                   :value="'custom'" v-model="request.photo_calculation">
                            <label class="custom-control-label" for="custom">{{ $t('blocks.custom') }}</label>
                        </div>
                        <form-errors :errors="errors" field="photo_calculation" />
                    </div>
                </div>

                <div class="form-group row"
                     v-if="request.photo_calculation === 'custom' && isPhotoBlock(edit_subblock) && !isFixedBlock(edit_subblock.parent_block)">
                    <label class="form-control-label col-4">{{ $t('blocks.duration_per_photo') }}*:</label>
                    <div class="col-8">
                        <input type="number" v-model="request.duration_per_photo" class="form-control-sm form-control">
                        <small>({{ $t('blocks.in_seconds') }})</small>
                        <form-errors :errors="errors" field="duration_per_photo" />
                    </div>
                </div>

                <div class="form-group row"
                     v-if="request.photo_calculation === 'custom' && hasMusic() && isPhotoBlock(edit_subblock)">
                    <label class="form-control-label col-4">{{ $t('blocks.repeat_photos') }}</label>
                    <div class="col-8">
                        <div class="custom-control custom-checkbox">
                            <input type="checkbox" class="custom-control-input" id="repeat_photos"
                                   v-model="request.repeat_photos">
                            <label class="custom-control-label" for="repeat_photos">Ja</label>
                        </div>
                        <form-errors :errors="errors" field="repeat_photos" />
                    </div>
                </div>

                <!-- Video settings -->
                <div class="form-group row" v-if="isVideoBlock(edit_subblock)">
                    <label class="form-control-label col-4">{{ $t('blocks.repeat_videos') }}</label>
                    <div class="col-8">
                        <div class="custom-control custom-checkbox">
                            <input type="checkbox" class="custom-control-input" id="repeat_videos"
                                   v-model="request.repeat_videos">
                            <label class="custom-control-label" for="repeat_videos">Ja</label>
                        </div>
                        <form-errors :errors="errors" field="repeat_videos" />
                    </div>
                </div>

                <!-- Background settings -->
                <div v-if="advancedBackgroundEnabled() && (isPhotoBlock(edit_subblock) || isVideoBlock(edit_subblock))" class="form-group row">
                    <label class="form-control-label col-4">{{ $t('blocks.background_settings') }}</label>
                    <div class="col-8">
                        <div class="custom-control custom-radio">
                            <input type="radio" id="color" name="background_type" class="custom-control-input" :value="'color'" v-model="request.background_type">
                            <label class="custom-control-label" for="color">{{ $t('blocks.choose_color') }}</label>
                        </div>
                        <div class="custom-control custom-radio">
                            <input type="radio" id="photo" name="background_type" class="custom-control-input" :value="'photo'" v-model="request.background_type">
                            <label class="custom-control-label" for="photo">{{ $t('blocks.fixed_photo_background') }}</label>
                        </div>
                        <div class="custom-control custom-radio">
                            <input type="radio" id="photo_blur" name="background_type" class="custom-control-input" :value="'photo_blur'" v-model="request.background_type">
                            <label class="custom-control-label" for="photo_blur">{{ isPhotoBlock(edit_subblock) ? $t('blocks.blur_photo_background') : $t('blocks.blur_video_background') }}</label>
                        </div>
                    </div>
                </div>

                <div v-if="advancedBackgroundEnabled() && request.background_type === 'color'" class="form-group row">
                    <label class="form-control-label col-4">{{ $t('blocks.background_color_label') }}</label>
                    <div class="col-8">
                        <label class="color-picker" :style="{'background-color': request.background_color}">
                            <input type="color" v-model="request.background_color" />
                        </label>
                    </div>
                </div>

                <div v-if="advancedBackgroundEnabled() && request.background_type === 'photo'" class="form-group row">
                    <label class="form-control-label col-4">{{ $t('blocks.background_photo_label') }}</label>
                    <div v-if="!request.background_photo" class="col-8">
                        <dropzone-upload
                            :url="`/api/ceremonies/code/${ceremony.code}/backgroundfiles/${parentBlock.id}`"
                            :extensions_text="'.png,.jpg,.jpeg,.gif,.heic,.webp'"
                            :icon-only="true"
                            :icon_text="$t('blocks.upload_photo')"
                            :unique_number_per_dropzone="99"
                            :max_files="1"
                            :max_size_mb_per_file="25"
                            :bestand="[]"
                            :unique_string_per_dropzone="'background_photo'"
                            @uploaded="photoUploaded"
                            @files-added="clearUploadErrors"
                            @dropzone_errors="setErrors($event)"
                            @clear_dropzone_errors="(errors.background_photo)">
                        </dropzone-upload>

                        <form-errors :errors="errors" field="background_photo" />
                    </div>
                    <div v-else class="col-8">
                        <file-block :file="request.background_photo"
                                    :ceremony-code="ceremony.code"
                                    :backgroundPhoto="true"
                                    @unlink="removeBackgroundPhoto()"
                        ></file-block>
                        <form-errors :errors="errors" field="background_photo" />
                    </div>
                </div>

                <!-- Transitions -->
                <div class="form-group row" v-if="playerVersion === 2">
                    <label class="form-control-label col-4">{{ $t('blocks.transition_type_label') }}</label>
                    <div class="col-8">
                        <select v-model="request.transition_type" class="form-control-sm form-control">
                            <option v-for="transition of transitions" :value="transition.type" :key="transition.type">{{ transition.displayName }}</option>
                        </select>
                        <form-errors :errors="errors" field="transition_type" />
                    </div>
                </div>
            </div>
        </div>

        <div slot="footer">
            <button type="button" class="btn btn-success btn-sm" @click="saveSubBlock">{{ $t('general.save') }}</button>
            <button type="button" class="btn btn-secondary btn-sm" data-dismiss="modal" @click="$emit('close_edit_subblock')">{{ $t('general.close') }}</button>
        </div>
    </modal>
</template>

<script>
    import { mapState } from 'vuex';
    import Modal from '../../components/Modal';
    import TimeInput from '../../components/TimeInput';
    import UtilMixin from '../mixins/UtilMixin';
    import FormErrors from '../../components/FormErrors';
    import DropzoneUpload from '../../components/DropzoneUpload';
    import FileBlock from '../../components/FileBlock.vue';
    import TRANSITIONS from '../../player-v2/components/transitions/index';
    import { TRANSITION_TYPE } from '../../player-v2/enums';
    import { notifySuccess } from '../../utils/notify';

    export default {
        name: 'SubBlockEdit',
        components: {
            FileBlock,
            TimeInput,
            Modal,
            FormErrors,
            DropzoneUpload,
        },
        mixins: [UtilMixin],

        data() {
            return {
                loading: false,
                show_modal: false,
                errors: {},
                request: {
                    name: '',
                    photo_calculation: null,
                    background_type: 'color',
                    background_color: '#000000',
                    background_photo: null,
                    transition_type: TRANSITION_TYPE.CROSS_FADE,
                },
            };
        },

        computed: {
            ...mapState({
                ceremony: state => state.ceremony,
                companySettings: state => state.companySettings,
            }),

            playerVersion() {
                return this.ceremony?.location?.player_version || 1;
            },

            transitions() {
                return Object.keys(TRANSITIONS).map(transitionType => {
                    return {
                        displayName: TRANSITIONS[transitionType].getDisplayName(),
                        type: transitionType,
                    };
                });
            },

            parentBlock() {
                return this.edit_subblock?.parent_block || this.block || null;
            }
        },

        props: {
            edit_subblock: {
                type: Object,
                required: true,
            },
            //parent block
            block: {
                type: Object,
                required: true,
            },
        },

        watch: {
            'edit_subblock.name'() {
                if (!this.edit_subblock) {
                    return;
                }

                this.request.name = this.edit_subblock.name;
            },
        },

        mounted() {
            // Check if the time value is overwritten. If so honor this value
            const settings = this.edit_subblock.settings || [];
            const photo_calculation = this.getSettingByName('photo_calculation', settings);

            if (photo_calculation) {
                this.request.photo_calculation = photo_calculation.value;
            } else if (this.getSettingByName('duration_per_photo', settings) && (this.block.files.length <= 0 || this.getSettingByName('duration_per_photo', settings).dirty === 1)) {
                this.request.photo_calculation = 'custom';
            } else {
                this.request.photo_calculation = 'music';
            }

            if (this.request.photo_calculation === 'music' && !this.hasMusic()) {
                this.request.photo_calculation = 'custom';
            }

            if (this.getSettingByName('duration_per_photo', settings)) {
                this.request.duration_per_photo = this.getSettingByName('duration_per_photo', settings).value || null;
            }

            if (this.getSettingByName('repeat_photos', settings)) {
                const repeat_photos = this.getSettingByName('repeat_photos', settings).value;
                this.request.repeat_photos = (parseInt(repeat_photos) === 1);
            }

            if (this.getSettingByName('repeat_videos', settings)) {
                const repeat_videos = this.getSettingByName('repeat_videos', settings).value;
                this.request.repeat_videos = (parseInt(repeat_videos) === 1);
            }

            if (this.getSettingByName('background_type', settings)) {
                this.request.background_type = this.getSettingByName('background_type', settings)?.value;
            }

            if (this.getSettingByName('background_color', settings)) {
                this.request.background_color = this.getSettingByName('background_color', settings)?.value;
            }

            if (this.getSettingByName('background_photo', settings)) {
                this.request.background_photo = this.getSettingByName('background_photo', settings)?.file;
            }

            if (this.getSettingByName('transition_type', settings)) {
                const value = this.getSettingByName('transition_type', settings)?.value;

                if (value) {
                    this.request.transition_type = value;
                } else {
                    this.request.transition_type = TRANSITION_TYPE.CROSS_FADE;
                }
            }

            this.request.name = this.edit_subblock.name;
        },

        methods: {

            async saveSubBlock() {
                this.errors = {};
                this.loading = true;

                let params = { ...this.request };

                // validate that the background photo is filled
                if (this.request.background_type === 'photo' && this.request.background_photo === null) {
                    this.errors.background_photo = ['Bij deze instelling is de achtergrond foto verplicht'];
                    return;
                } else if (this.request.background_photo !== null) {
                    // to prevent errors in the backend parse the photo object to json
                    params.background_photo = this.request.background_photo?.id;
                }

                try {
                    await this.$http.post(`/api/ceremonies/code/${this.ceremony.code}/blocks/${this.edit_subblock.id}/save-settings`, params);

                    this.$emit('load-block', this.block);
                    this.$emit('close');

                    notifySuccess(this.$t('blocks.block_saved_title'));
                } catch (exception) {
                    if (exception.response && exception.response.status === 422) {
                        this.errors = exception.response.data.errors;
                    } else {
                        if (!exception.response || (exception.response && exception.response.status !== 401)) {
                            alert('Cannot save block');
                        }
                    }
                }

                this.loading = false;

            },

            hasMusic() {
                return this.parentBlock.files.length > 0;
            },

            photoUploaded(file, response) {
                this.request.background_photo = response.file_model;
            },

            setErrors(error) {
                this.errors.background_photo = error;
                this.$forceUpdate();
            },

            advancedBackgroundEnabled() {
                return this.companySettings.advanced_background_enabled;
            },

            async removeBackgroundPhoto() {
                // remove photo from storage
                const fileId = this.request.background_photo ? this.request.background_photo.id : null;
                if (fileId !== null) {
                    try {
                        await this.$http.delete(`/api/ceremonies/code/${this.ceremony.code}/files/${fileId}`, this.request);
                    } catch (exception) {
                        console.error(exception);
                    }
                }

                this.request.background_photo = null;

                await this.saveSubBlock();
            },

            clearUploadErrors() {
                this.errors = {
                    ...this.errors,
                    background_photo: [],
                };
            },
        },
    };

</script>

<style scoped>

    .video-repeat-info {
        line-height: 14px;
        display: block;
        margin-bottom: 15px;
    }

    .color-picker {
        width: 70px;
        border: none;
        padding: 0;
        appearance: none;
        background: none;
        cursor: pointer;
        border-radius: 3px;
        margin: 0;
    }

    .color-picker input {
        opacity: 0;
        pointer-events: none;
    }
</style>
